import { client } from './api';

/**
 * Saying what a fax is, labelling it, and whose it is.
 *
 * The types come from Roger's list and are read-only here: a type that one
 * agency could rename would stop meaning the same thing everywhere, which is
 * the whole reason to have types rather than only tags. Tags are the agency's
 * own and are created from the same picker that applies them.
 */

export interface FaxTypeOption {
  id: string;
  name: string;
  /** Retired types come back only when asked for, and only for filtering. */
  is_active: boolean;
}

export interface FaxTag {
  id: string;
  name: string;
}

export interface PatientOption {
  id: string;
  first_name: string;
  last_name: string;
  mrn: string | null;
}

export interface AdmissionOption {
  id: string;
  soc_date: string;
  discharge_date: string | null;
  status: string;
}

/** What a row is, stated whole. */
export interface Classification {
  fax_type_id: string | null;
  patient_id: string | null;
  admission_id: string | null;
  /**
   * The draft referral this fax is being gathered for, when the patient does
   * not exist yet. Exclusive with the patient above, and the server refuses a
   * body that names both.
   */
  intake_draft_id: string | null;
  tag_ids: string[];
}

export type FaxItemKind = 'arrival' | 'document';

/**
 * The types on offer, and optionally the retired ones too.
 *
 * Choosing a type for a fax gets the offered ones. Filtering a list by type
 * gets all of them, because retiring a type must not hide the faxes already
 * filed under it: they still say what they are.
 */
export async function listFaxTypeOptions(
  includeRetired = false
): Promise<FaxTypeOption[]> {
  const response = await client.get<FaxTypeOption[], unknown, true>({
    query: { include_retired: includeRetired },
    responseType: 'json',
    throwOnError: true,
    url: '/fax-classification/types',
  });
  return response.data;
}

export async function listFaxTags(q?: string): Promise<FaxTag[]> {
  const response = await client.get<FaxTag[], unknown, true>({
    query: { q },
    responseType: 'json',
    throwOnError: true,
    url: '/fax-classification/tags',
  });
  return response.data;
}

/**
 * Add a tag.
 *
 * A name that already exists comes back as the existing tag rather than an
 * error, so somebody typing one they already have simply gets it applied.
 */
export async function createFaxTag(name: string): Promise<FaxTag> {
  const response = await client.post<FaxTag, unknown, true>({
    body: { name },
    responseType: 'json',
    throwOnError: true,
    url: '/fax-classification/tags',
  });
  return response.data;
}

export async function deleteFaxTag(tagId: string): Promise<void> {
  await client.delete<void, unknown, true>({
    responseType: 'json',
    throwOnError: true,
    url: `/fax-classification/tags/${tagId}`,
  });
}

export async function listPatientAdmissions(
  patientId: string
): Promise<AdmissionOption[]> {
  const response = await client.get<AdmissionOption[], unknown, true>({
    responseType: 'json',
    throwOnError: true,
    url: `/fax-classification/patients/${patientId}/admissions`,
  });
  return response.data;
}

/**
 * Save the whole classification.
 *
 * Every field goes on every save rather than only the changed ones: a half
 * stated classification cannot say the difference between leaving the patient
 * alone and there being no patient.
 */
export async function setClassification(
  kind: FaxItemKind,
  itemId: string,
  classification: Classification
): Promise<Classification> {
  const response = await client.put<Classification, unknown, true>({
    body: classification,
    responseType: 'json',
    throwOnError: true,
    url: `/fax-classification/${kind}/${itemId}`,
  });
  return response.data;
}

/**
 * Take a fax out of the inbox.
 *
 * The pages stay and anything already built from them keeps its origin; the
 * inbox simply stops showing it.
 */
export async function removeFaxItem(
  kind: FaxItemKind,
  itemId: string
): Promise<void> {
  await client.delete<void, unknown, true>({
    responseType: 'json',
    throwOnError: true,
    url: `/fax-classification/${kind}/${itemId}`,
  });
}

/**
 * Patients matching what somebody typed.
 *
 * Hand written against the client like the rest of this screen's calls, so
 * the inbox's API surface stays in one file and a client regeneration does
 * not drag unrelated routes into the diff.
 *
 * Capped small: this feeds a picker somebody is typing into, not a list they
 * page through, and a wide search on a large agency is a slow query nobody
 * reads the bottom of.
 */
interface PatientPage {
  items: PatientOption[];
}

export async function searchPatients(search: string): Promise<PatientOption[]> {
  const response = await client.get<PatientPage, unknown, true>({
    query: { search, page: 1, page_size: 20, show_inactive: true },
    responseType: 'json',
    throwOnError: true,
    url: '/patients/',
  });
  return response.data.items;
}

/**
 * Take several faxes out of the inbox at once.
 *
 * All or nothing: clearing a screenful of junk is one decision, and a partial
 * result would leave somebody reading the list to work out which half went.
 */
interface RemovedCount {
  removed: number;
}

export async function removeFaxItems(
  items: { kind: FaxItemKind; id: string }[]
): Promise<number> {
  const response = await client.post<RemovedCount, unknown, true>({
    body: { items },
    responseType: 'json',
    throwOnError: true,
    url: '/fax-classification/remove',
  });
  return response.data.removed;
}
